import { useCallback } from 'react';
import { Avatar, Space, Typography, Tooltip } from 'antd';
import { dayjs } from '../utils/dayjs';

const { Text } = Typography;

interface UseUserTimestampRenderOptions {
  size?: 'small' | 'default' | 'large';
  showOnlyCreated?: boolean;
}

export const useUserTimestampRender = (
  options: UseUserTimestampRenderOptions = {}
) => {
  const { size = 'default', showOnlyCreated = false } = options;

  return useCallback(
    (record: any) => {
      // Handle undefined/null record
      if (!record) {
        return null;
      }

      const { creator, updater, created_at, updated_at, ...rest } = record;

      // Get display names from creator/updater objects
      const creatorName =
        creator?.first_name && creator?.last_name
          ? `${creator.first_name} ${creator.last_name}`
          : 'N/A';
      const updaterName =
        updater?.first_name && updater?.last_name
          ? `${updater.first_name} ${updater.last_name}`
          : null;

      // Determine which user and timestamp to show (updated first, then created)
      // If showOnlyCreated is true, always show created data
      const hasUpdatedData = showOnlyCreated
        ? false
        : !!(updaterName && updated_at);
      const displayUser = hasUpdatedData ? updaterName : creatorName;
      const displayTimestamp = hasUpdatedData ? updated_at : created_at;
      const isUpdated = hasUpdatedData;

      // Additional safety check for required data
      if (!displayUser || !displayTimestamp) {
        return null;
      }

      // Generate initials from name
      const getInitials = (name: string) => {
        return name
          .split(' ')
          .map((part: string) => part.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      };

      // Format timestamp for display
      const formatTimestamp = (timestamp: string | Date) => {
        const date = dayjs(timestamp);
        const now = dayjs();

        // Check if date is valid
        if (!date.isValid()) {
          return 'Invalid date';
        }

        const timeStr = date.format('h:mm');
        const ampm = date.hour() >= 12 ? 'PM' : 'AM';

        if (date.isSame(now, 'day')) {
          return `Today at ${timeStr} ${ampm}`;
        } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
          return `Yesterday at ${timeStr} ${ampm}`;
        } else if (date.isSame(now, 'year')) {
          return `${date.format('MMM DD')} at ${timeStr} ${ampm}`;
        } else {
          return `${date.format('MMM DD, YYYY')} at ${timeStr} ${ampm}`;
        }
      };

      // Format full timestamp for tooltip
      const formatFullTimestamp = (timestamp: string | Date) => {
        const date = dayjs(timestamp);
        if (!date.isValid()) {
          return 'Invalid date';
        }
        const timeStr = date.format('h:mm:ss');
        const ampm = date.hour() >= 12 ? 'PM' : 'AM';
        return `${date.format('MMMM DD, YYYY')} at ${timeStr} ${ampm}`;
      };

      const initials = getInitials(displayUser);
      const avatarSize = size === 'small' ? 24 : size === 'large' ? 40 : 32;
      const fontSize =
        size === 'small' ? '10px' : size === 'large' ? '14px' : '12px';

      const tooltipContent = (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {displayUser}
          </div>
          <div>
            {isUpdated ? 'Updated ' : 'Created '}
            {formatFullTimestamp(displayTimestamp)}
          </div>
        </div>
      );

      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 8,
              cursor: 'help',
            }}
          >
            <Space>
              <Avatar
                size={avatarSize}
                style={{
                  backgroundColor: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontSize:
                      size === 'small'
                        ? '10px'
                        : size === 'large'
                          ? '16px'
                          : '12px',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {initials}
                </span>
              </Avatar>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text
                  style={{
                    fontSize: size === 'small' ? '12px' : '12px',
                    color: '#8c8c8c',
                    margin: 0,
                    display: 'block',
                  }}
                >
                  {isUpdated ? 'Updated ' : 'Created '}
                  {formatTimestamp(displayTimestamp)}
                </Text>
              </div>
            </Space>
          </div>
        </Tooltip>
      );
    },
    [size, showOnlyCreated]
  );
};
